// Utils
import { isValidAliasParent } from './breadcrumbAliasUtil';
import {
    chooseBreadcrumb,
    createCompleteBreadcrumb,
    getReadableBreadcrumb,
    getReadablePath,
} from './breadcrumbResultUtil';

// Types
import { IdGraph } from '../../../../common/dataStructures/graphTypes';
import { ImMNElementMap } from '../../../../common/elements/elementModelTypes';
import { BreadcrumbResult, PreferredAliasIdByBoardId } from '../breadcrumbTypes';

/**
 * Returns a preferred alias only when it still points to the board that saved it.
 */
const getValidPreferredAliasId = (
    boardId: string,
    elements: ImMNElementMap,
    boardIdToAliasIdsMap: IdGraph,
    preferredAliasIdByBoardId: PreferredAliasIdByBoardId,
): string | undefined => {
    const preferredAliasId = preferredAliasIdByBoardId[boardId];

    if (!preferredAliasId) return;
    if (!boardIdToAliasIdsMap[boardId]?.includes(preferredAliasId)) return;
    if (!isValidAliasParent(elements, preferredAliasId, boardId)) return;

    return preferredAliasId;
};

/**
 * Builds the best breadcrumb for the alias itself, following nested preferred aliases first.
 */
const getBreadcrumbForPreferredAlias = (
    aliasId: string,
    rootBoardId: string,
    elements: ImMNElementMap,
    aclIds: string[],
    boardIdToAliasIdsMap: IdGraph,
    preferredAliasIdByBoardId: PreferredAliasIdByBoardId,
    visitedAliasIds: Set<string>,
): BreadcrumbResult | undefined => {
    const readableAliasBreadcrumb = getReadableBreadcrumb(aliasId, rootBoardId, elements, aclIds);

    if (visitedAliasIds.has(aliasId)) return readableAliasBreadcrumb;

    // recursive calls mean getPreferredAliasBreadcrumb needs to be called before it's defined, but don't worry, it's safe
    // eslint-disable-next-line
    const preferredAliasBreadcrumb = getPreferredAliasBreadcrumb(
        aliasId,
        rootBoardId,
        elements,
        aclIds,
        boardIdToAliasIdsMap,
        preferredAliasIdByBoardId,
        new Set([...visitedAliasIds, aliasId]),
    );

    return chooseBreadcrumb(preferredAliasBreadcrumb, readableAliasBreadcrumb);
};

/**
 * Builds a breadcrumb that enters a board through the preferred alias chosen by navigation.
 */
export const getPreferredAliasBreadcrumb = (
    currentBoardId: string,
    rootBoardId: string,
    elements: ImMNElementMap,
    aclIds: string[],
    boardIdToAliasIdsMap: IdGraph,
    preferredAliasIdByBoardId?: PreferredAliasIdByBoardId,
    visitedAliasIds = new Set<string>(),
): BreadcrumbResult | undefined => {
    if (!preferredAliasIdByBoardId) return;

    const readablePath = getReadablePath(currentBoardId, elements, aclIds);

    if (!readablePath.length) return;

    // Walk from the current board back toward the readable root so the deepest saved alias wins first.
    // This preserves the path the user just took when multiple boards in the shared branch have aliases.
    for (let boardIndex = readablePath.length - 1; boardIndex >= 0; boardIndex--) {
        const boardId = readablePath[boardIndex];
        const preferredAliasId = getValidPreferredAliasId(
            boardId,
            elements,
            boardIdToAliasIdsMap,
            preferredAliasIdByBoardId,
        );

        if (!preferredAliasId) continue;

        // Build the path to the alias itself, then append the readable physical path from the aliased board down.
        const aliasBreadcrumb = getBreadcrumbForPreferredAlias(
            preferredAliasId,
            rootBoardId,
            elements,
            aclIds,
            boardIdToAliasIdsMap,
            preferredAliasIdByBoardId,
            visitedAliasIds,
        );

        if (!aliasBreadcrumb?.complete || !aliasBreadcrumb.fullPath) continue;

        const fullPath = [...aliasBreadcrumb.fullPath, ...readablePath.slice(boardIndex)];

        return createCompleteBreadcrumb(fullPath, elements, aliasBreadcrumb.detached);
    }
};
